/**
 * OneAmerica AssetCare Pricing Utilities
 * Shared types, interpolation logic, and lookup functions used by
 * the category-specific flat and demographic calibration tables.
 */

import { FundingPolicyType, getQuotePayYears } from '@shared';
import {
  OA_CAT1_DEMOGRAPHIC_BANDS,
  OA_CAT1_DEMOGRAPHIC_SINGLE_PAY_AGES,
} from './oneAmericaCat1DemographicBands';
import {
  OA_CAT2_DEMO_BANDS,
  OA_CAT2_AVAILABLE_AGES,
  OA_CAT2_MIXED_MIN_AGES,
  OA_CAT2_MIXED_MAX_AGES,
} from './oneAmericaCat2DemoBands';
import {
  OA_CAT2_DEMOGRAPHIC_SINGLE_PAY_AGES,
  OA_CAT2_DEMOGRAPHIC_SINGLE_PAY_BANDS,
} from './oneAmericaCat2DemographicSinglePayBands';
import {
  OA_CAT3_DEMOGRAPHIC_SINGLE_PAY_AGES,
  OA_CAT3_DEMOGRAPHIC_SINGLE_PAY_BANDS,
} from './oneAmericaCat3DemographicSinglePayBands';
import { OA_CAT3_DEMO_BANDS, OA_CAT3_AVAILABLE_AGES } from './oneAmericaCat3DemoBands';
import { OA_BANDS_BY_CATEGORY } from './oneAmericaPricingConfig';

export type BandEntry = { premium: number; db: number; ltc: number };
export type PackedBandEntry = readonly [premium: number, db: number, ltc: number];
type BandValue = BandEntry | PackedBandEntry;
export type ConfigBands = Record<string, BandEntry[]>;

/**
 * Why a band lookup could not price a premium. The cases need different handling, so they are
 * distinguished rather than collapsed into one "out of range" flag:
 *
 * - 'above-band': a table applies, but the premium is above its last row. Flat-clamping the
 *   edge multiplier here is what let benefits scale linearly to any premium while ROI stayed
 *   constant (the A2 defect), so callers must refuse to reprice.
 * - 'no-band': no table exists for this state category, product, config, or demographic. The
 *   multipliers are inert 1.0s, which yields plain linear scaling — the prior behavior for
 *   these rows, and bounded now that the premium is always clamped to known bounds.
 * - 'unpriceable': a demographic Single Pay table should apply, but no validated curve matched.
 *   Callers must preserve the carrier quote instead of inventing a linear replacement.
 *
 * Premiums *below* the first row are deliberately not a miss: they keep the flat clamp. That
 * direction shrinks benefits rather than inflating them, is already floored by investmentMin,
 * and is the common real case — measured across the quote table, 0 OneAmerica rows sit above
 * their table's last band at their own premium while 44.4% of category-3 rows sit below the
 * first. Refusing those would have stranded ~37k quotes for no safety gain.
 */
export type OABandMiss = 'above-band' | 'no-band' | 'unpriceable';

export type OAMultipliers = {
  db: number;
  ltc: number;
  miss?: OABandMiss;
  maximumPremium?: number;
};

const OA_MULTIPLIER_FALLBACK: OAMultipliers = { db: 1.0, ltc: 1.0, miss: 'no-band' };
const OA_UNPRICEABLE_FALLBACK: OAMultipliers = { db: 1.0, ltc: 1.0, miss: 'unpriceable' };

function combineMiss(a: OAMultipliers, b: OAMultipliers): OABandMiss | undefined {
  if (a.miss === 'unpriceable' || b.miss === 'unpriceable') return 'unpriceable';
  if (a.miss === 'above-band' || b.miss === 'above-band') return 'above-band';
  return a.miss ?? b.miss;
}

function combineMaximumPremium(a: OAMultipliers, b: OAMultipliers): number | undefined {
  if (a.maximumPremium === undefined) return b.maximumPremium;
  if (b.maximumPremium === undefined) return a.maximumPremium;
  return Math.min(a.maximumPremium, b.maximumPremium);
}

function isPackedBandEntry(entry: BandValue): entry is PackedBandEntry {
  return Array.isArray(entry);
}

function toPackedBandEntry(entry: BandValue): PackedBandEntry {
  if (isPackedBandEntry(entry)) return entry;
  const { premium, db, ltc } = entry;
  return [premium, db, ltc];
}

export function interpolateBand(bands: readonly BandValue[], premium: number): OAMultipliers {
  if (bands.length === 0) return OA_MULTIPLIER_FALLBACK;

  const [firstPremium, firstDb, firstLtc] = toPackedBandEntry(bands[0]);
  const [lastPremium, lastDb, lastLtc] = toPackedBandEntry(bands[bands.length - 1]);

  if (premium > lastPremium) {
    return { db: lastDb, ltc: lastLtc, miss: 'above-band', maximumPremium: lastPremium };
  }
  if (premium <= firstPremium) return { db: firstDb, ltc: firstLtc, maximumPremium: lastPremium };
  if (premium === lastPremium) return { db: lastDb, ltc: lastLtc, maximumPremium: lastPremium };

  for (let i = 0; i < bands.length - 1; i++) {
    const [lowerPremium, lowerDb, lowerLtc] = toPackedBandEntry(bands[i]);
    const [upperPremium, upperDb, upperLtc] = toPackedBandEntry(bands[i + 1]);
    if (premium >= lowerPremium && premium < upperPremium) {
      const t = (premium - lowerPremium) / (upperPremium - lowerPremium);
      return {
        db: lowerDb + t * (upperDb - lowerDb),
        ltc: lowerLtc + t * (upperLtc - lowerLtc),
        maximumPremium: lastPremium,
      };
    }
  }

  return OA_MULTIPLIER_FALLBACK;
}

const OA_ASSET_CARE_TYPES = new Set([
  FundingPolicyType.assetCare,
  FundingPolicyType.jointAssetCare,
]);

const OA_JOINT_TYPES = new Set([FundingPolicyType.jointAssetCare]);

export function isOneAmericaAssetCare(fundingPolicyType: number): boolean {
  return OA_ASSET_CARE_TYPES.has(fundingPolicyType);
}

function normalizeInflationValue(value: string | undefined | null): string {
  if (
    !value ||
    value === 'null' ||
    value.trim() === '' ||
    value.trim() === '0%' ||
    value.trim() === '0'
  )
    return 'None';
  return value;
}

function normalizeDemographicValue(value: string | undefined | null): string {
  return (
    value
      ?.trim()
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '') ?? ''
  );
}

function getDemographicMultipliers(
  bandsByKey: Record<string, readonly BandValue[]>,
  availableAges: readonly number[],
  productKey: string,
  configKey: string,
  premium: number,
  age: number | undefined,
  tobaccoStatus: string | undefined,
  inflationDuration: string | undefined | null,
  gender?: string,
): OAMultipliers | undefined {
  if (productKey !== 'sp_single' || !age || !tobaccoStatus) return undefined;

  const [aobInflation, cobInflation] = configKey.split('|');
  const hasInflation =
    normalizeDemographicValue(aobInflation) !== 'none' ||
    normalizeDemographicValue(cobInflation) !== 'none';
  const duration = hasInflation ? normalizeDemographicValue(inflationDuration) : 'none';
  if (!duration) return undefined;

  const genderPrefix = gender ? `${normalizeDemographicValue(gender)}|` : '';
  const suffix = [normalizeDemographicValue(tobaccoStatus), duration, productKey, configKey].join(
    '|',
  );
  const bandsForAge = (candidateAge: number): readonly BandValue[] | undefined =>
    bandsByKey[`${genderPrefix}${candidateAge}|${suffix}`];

  const exact = bandsForAge(age);
  if (exact) return interpolateBand(exact, premium);

  const [lowerAge, upperAge] = findBracket(age, availableAges);
  const lowerBands = bandsForAge(lowerAge);
  if (lowerAge === upperAge) {
    return lowerBands ? interpolateBand(lowerBands, premium) : undefined;
  }

  const upperBands = bandsForAge(upperAge);
  if (!lowerBands) return upperBands ? interpolateBand(upperBands, premium) : undefined;
  if (!upperBands) return interpolateBand(lowerBands, premium);

  return lerp(
    interpolateBand(lowerBands, premium),
    interpolateBand(upperBands, premium),
    (age - lowerAge) / (upperAge - lowerAge),
  );
}

/**
 * The band tables are calibrated for exactly two pay structures: single premium
 * and 10-Pay. resolveProductKey below keys off `productFundingOption`, which reads
 * 'Recurring Premium' for EVERY OneAmerica recurring row regardless of its term,
 * so a 20-Pay quote resolves to the 10pay_* curves and is silently repriced with
 * the wrong carrier calibration — no missing-key error to catch it.
 *
 * `cobPaymentOption` carries the real term ('10-Pay', '20-Pay'), so it is the
 * discriminator. A null pay-years (single premium, lifetime, monthly, annual) is
 * calibrated via the sp_* tables; only a fixed-year term other than 10 is not.
 * Callers must refuse to reprice those rather than fall through to a wrong curve.
 */
export function hasCalibratedOABands(
  cobPaymentOption: string | undefined | null,
  productFundingOption: string | undefined | null,
): boolean {
  const payYears = getQuotePayYears({ cobPaymentOption, productFundingOption });
  return payYears === null || payYears === 10;
}

export function resolveProductKey(
  fundingPolicyType: number,
  productFundingOption: string | undefined,
): string {
  const isJoint = OA_JOINT_TYPES.has(fundingPolicyType);
  const is10Pay =
    productFundingOption?.toLowerCase().includes('recurring') ||
    productFundingOption?.toLowerCase().includes('10');
  if (is10Pay) return isJoint ? '10pay_joint' : '10pay_single';
  return isJoint ? 'sp_joint' : 'sp_single';
}

export function buildConfigKey(
  aobInflation: string | undefined | null,
  cobInflation: string | undefined | null,
  cobDuration: string | undefined | null,
): string {
  const aob = normalizeInflationValue(aobInflation);
  const cob = normalizeInflationValue(cobInflation);
  const dur = cobDuration?.trim() || '2 Years';
  return `${aob}|${cob}|${dur}`;
}

export function getOAMultipliersForPremium(
  fundingPolicyType: number,
  productFundingOption: string | undefined,
  aobInflation: string | undefined | null,
  cobInflation: string | undefined | null,
  cobDuration: string | undefined | null,
  premium: number,
  usaStateCategory: number | undefined,
  primaryAge?: number,
  primaryGender?: string,
  aobDuration?: string | undefined | null,
  secondaryAge?: number,
  primaryTobaccoStatus?: string,
  inflationDuration?: string | undefined | null,
): OAMultipliers {
  const productKey = resolveProductKey(fundingPolicyType, productFundingOption);
  const isJoint = OA_JOINT_TYPES.has(fundingPolicyType);

  if (usaStateCategory === 1) {
    const configKey = buildConfigKey(aobInflation, cobInflation, cobDuration);
    const demographic = getDemographicMultipliers(
      OA_CAT1_DEMOGRAPHIC_BANDS,
      OA_CAT1_DEMOGRAPHIC_SINGLE_PAY_AGES,
      productKey,
      configKey,
      premium,
      primaryAge,
      primaryTobaccoStatus,
      inflationDuration,
      primaryGender,
    );
    if (demographic) return demographic;
  }

  if (usaStateCategory === 3 && primaryAge && primaryGender) {
    const configKey = buildConfigKey(aobInflation, cobInflation, cobDuration);
    const [aob, cob] = configKey.split('|');
    const isCategory3Configuration = aob !== '2%' && cob !== '2%';
    const demographic = isCategory3Configuration
      ? getDemographicMultipliers(
          OA_CAT3_DEMOGRAPHIC_SINGLE_PAY_BANDS,
          OA_CAT3_DEMOGRAPHIC_SINGLE_PAY_AGES,
          productKey,
          configKey,
          premium,
          primaryAge,
          primaryTobaccoStatus,
          inflationDuration,
          primaryGender,
        )
      : undefined;
    if (demographic) return demographic;
    if (productKey !== 'sp_single') {
      return getCat3DemoMultipliers(productKey, primaryGender, primaryAge, configKey, premium);
    }
  }

  if (usaStateCategory === 2 && primaryAge) {
    const aob = normalizeInflationValue(aobInflation);
    const cob = normalizeInflationValue(cobInflation);
    const aobDur = aobDuration?.trim() || '';
    const cobDur = cobDuration?.trim() || '';
    const configKey = `${aob}|${cob}|${aobDur}|${cobDur}`;
    const demographic = getDemographicMultipliers(
      OA_CAT2_DEMOGRAPHIC_SINGLE_PAY_BANDS,
      OA_CAT2_DEMOGRAPHIC_SINGLE_PAY_AGES,
      productKey,
      configKey,
      premium,
      primaryAge,
      primaryTobaccoStatus,
      inflationDuration,
    );
    if (demographic) return demographic;
    if (productKey !== 'sp_single') {
      return getCat2DemoMultipliers(
        productKey,
        primaryAge,
        configKey,
        premium,
        isJoint ? secondaryAge : undefined,
      );
    }
  }

  // Single Pay Single Life now uses validated demographic inventories for categories 1–3.
  // If one did not match, falling through to inert multipliers would fabricate a linear quote.
  if (productKey === 'sp_single') return OA_UNPRICEABLE_FALLBACK;

  const configKey = buildConfigKey(aobInflation, cobInflation, cobDuration);
  const categoryBands = usaStateCategory ? OA_BANDS_BY_CATEGORY[usaStateCategory] : undefined;
  if (!categoryBands) return OA_MULTIPLIER_FALLBACK;

  const configBands = categoryBands[productKey];
  if (!configBands) return OA_MULTIPLIER_FALLBACK;

  const bands = configBands[configKey];
  if (!bands) return OA_MULTIPLIER_FALLBACK;

  return interpolateBand(bands, premium);
}

function findBracket(age: number, ages: readonly number[]): [number, number] {
  if (age <= ages[0]) return [ages[0], ages[0]];
  if (age >= ages[ages.length - 1]) return [ages[ages.length - 1], ages[ages.length - 1]];
  for (let i = 0; i < ages.length - 1; i++) {
    if (age >= ages[i] && age <= ages[i + 1]) return [ages[i], ages[i + 1]];
  }
  return [ages[0], ages[ages.length - 1]];
}

function lerp(a: OAMultipliers, b: OAMultipliers, t: number): OAMultipliers {
  return {
    db: a.db + t * (b.db - a.db),
    ltc: a.ltc + t * (b.ltc - a.ltc),
    miss: combineMiss(a, b),
    maximumPremium: combineMaximumPremium(a, b),
  };
}

function lookupSameAge(
  productKey: string,
  age: number,
  configKey: string,
  premium: number,
): OAMultipliers {
  const exactKey = `${productKey}|${age}|${configKey}`;
  const exactBands = OA_CAT2_DEMO_BANDS[exactKey];
  if (exactBands) return interpolateBand(exactBands, premium);

  const [lo, hi] = findBracket(age, OA_CAT2_AVAILABLE_AGES);
  if (lo === hi) {
    const bands = OA_CAT2_DEMO_BANDS[`${productKey}|${lo}|${configKey}`];
    return bands ? interpolateBand(bands, premium) : OA_MULTIPLIER_FALLBACK;
  }
  const loBands = OA_CAT2_DEMO_BANDS[`${productKey}|${lo}|${configKey}`];
  const hiBands = OA_CAT2_DEMO_BANDS[`${productKey}|${hi}|${configKey}`];
  if (!loBands && !hiBands) return OA_MULTIPLIER_FALLBACK;
  if (!loBands) return interpolateBand(hiBands!, premium);
  if (!hiBands) return interpolateBand(loBands, premium);
  const t = (age - lo) / (hi - lo);
  return lerp(interpolateBand(loBands, premium), interpolateBand(hiBands, premium), t);
}

function lookupMixedAge(
  productKey: string,
  minAge: number,
  maxAge: number,
  configKey: string,
  premium: number,
): OAMultipliers | null {
  const exactKey = `${productKey}|${minAge}|${maxAge}|${configKey}`;
  const exactBands = OA_CAT2_DEMO_BANDS[exactKey];
  if (exactBands) return interpolateBand(exactBands, premium);

  const [minLo, minHi] = findBracket(minAge, OA_CAT2_MIXED_MIN_AGES);
  const [maxLo, maxHi] = findBracket(maxAge, OA_CAT2_MIXED_MAX_AGES);

  const corners: (OAMultipliers | null)[] = [
    OA_CAT2_DEMO_BANDS[`${productKey}|${minLo}|${maxLo}|${configKey}`],
    OA_CAT2_DEMO_BANDS[`${productKey}|${minLo}|${maxHi}|${configKey}`],
    OA_CAT2_DEMO_BANDS[`${productKey}|${minHi}|${maxLo}|${configKey}`],
    OA_CAT2_DEMO_BANDS[`${productKey}|${minHi}|${maxHi}|${configKey}`],
  ].map(b => (b ? interpolateBand(b, premium) : null));

  const available = corners.filter((c): c is OAMultipliers => c !== null);
  if (available.length === 0) return null;
  if (available.length === 1) return available[0];

  if (available.length === 4) {
    const tMin = minLo === minHi ? 0 : (minAge - minLo) / (minHi - minLo);
    const tMax = maxLo === maxHi ? 0 : (maxAge - maxLo) / (maxHi - maxLo);
    const bot = lerp(corners[0]!, corners[1]!, tMax);
    const top = lerp(corners[2]!, corners[3]!, tMax);
    return lerp(bot, top, tMin);
  }

  const sum = available.reduce((a, c) => ({ db: a.db + c.db, ltc: a.ltc + c.ltc }), {
    db: 0,
    ltc: 0,
  });
  return {
    db: sum.db / available.length,
    ltc: sum.ltc / available.length,
    miss: available.reduce<OABandMiss | undefined>(
      (acc, corner) => combineMiss({ db: 0, ltc: 0, miss: acc }, corner),
      undefined,
    ),
    maximumPremium: available.reduce<number | undefined>(
      (value, corner) => combineMaximumPremium({ db: 0, ltc: 0, maximumPremium: value }, corner),
      undefined,
    ),
  };
}

export function getCat2DemoMultipliers(
  productKey: string,
  primaryAge: number,
  configKey: string,
  premium: number,
  secondaryAge?: number,
): OAMultipliers {
  const isJoint = productKey.includes('joint');
  const secAge = secondaryAge ?? primaryAge;

  if (isJoint && secAge !== primaryAge) {
    const minAge = Math.min(primaryAge, secAge);
    const maxAge = Math.max(primaryAge, secAge);
    const mixedResult = lookupMixedAge(productKey, minAge, maxAge, configKey, premium);
    if (mixedResult) return mixedResult;
    return lookupSameAge(productKey, maxAge, configKey, premium);
  }

  return lookupSameAge(productKey, primaryAge, configKey, premium);
}

function findBracketAges(age: number): [number, number] {
  const ages = OA_CAT3_AVAILABLE_AGES;
  if (age <= ages[0]) return [ages[0], ages[0]];
  if (age >= ages[ages.length - 1]) return [ages[ages.length - 1], ages[ages.length - 1]];
  for (let i = 0; i < ages.length - 1; i++) {
    if (age >= ages[i] && age <= ages[i + 1]) return [ages[i], ages[i + 1]];
  }
  return [ages[0], ages[ages.length - 1]];
}

export function getCat3DemoMultipliers(
  productKey: string,
  gender: string,
  age: number,
  configKey: string,
  premium: number,
): OAMultipliers {
  const normalizedGender = gender === 'Female' ? 'Female' : 'Male';

  const exactKey = `${productKey}|${normalizedGender}|${age}|${configKey}`;
  const exactBands = OA_CAT3_DEMO_BANDS[exactKey];
  if (exactBands) return interpolateBand(exactBands, premium);

  const [ageLow, ageHigh] = findBracketAges(age);
  if (ageLow === ageHigh) {
    const key = `${productKey}|${normalizedGender}|${ageLow}|${configKey}`;
    const bands = OA_CAT3_DEMO_BANDS[key];
    return bands ? interpolateBand(bands, premium) : OA_MULTIPLIER_FALLBACK;
  }

  const lowKey = `${productKey}|${normalizedGender}|${ageLow}|${configKey}`;
  const highKey = `${productKey}|${normalizedGender}|${ageHigh}|${configKey}`;
  const lowBands = OA_CAT3_DEMO_BANDS[lowKey];
  const highBands = OA_CAT3_DEMO_BANDS[highKey];

  if (!lowBands && !highBands) return OA_MULTIPLIER_FALLBACK;
  if (!lowBands) return interpolateBand(highBands!, premium);
  if (!highBands) return interpolateBand(lowBands, premium);

  const lowResult = interpolateBand(lowBands, premium);
  const highResult = interpolateBand(highBands, premium);
  const t = (age - ageLow) / (ageHigh - ageLow);
  return {
    db: lowResult.db + t * (highResult.db - lowResult.db),
    ltc: lowResult.ltc + t * (highResult.ltc - lowResult.ltc),
    miss: combineMiss(lowResult, highResult),
    maximumPremium: combineMaximumPremium(lowResult, highResult),
  };
}
